import { inject, Injectable } from '@angular/core';
import { EntityNameType } from '@core/interfaces/entity-name-type';
import { ConnectionProfilesService } from '@core/services/connection-profiles/connection-profiles.service';
import { ModulesService } from '@core/services/modules/modules.service';
import { ProjectsService } from '@core/services/projects/projects.service';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { EMPTY, Observable } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import {
  AddEntityName,
  LoadEntityNames,
  RemoveEntityName,
  UpdateEntityName,
} from './entity-names.actions';

export interface EntityNamesEntry {
  readonly names: readonly string[];
  readonly loaded: boolean;
}

export interface EntityNamesStateModel {
  readonly entries: Record<EntityNameType, EntityNamesEntry>;
}

type NamesResponse = { readonly names?: readonly string[] | undefined };

type NamesService = {
  names(): Observable<NamesResponse>;
};

const DEFAULT_ENTRY: EntityNamesEntry = { names: [], loaded: false };

const DEFAULT_STATE: EntityNamesStateModel = {
  entries: {
    [EntityNameType.Project]: DEFAULT_ENTRY,
    [EntityNameType.Module]: DEFAULT_ENTRY,
    [EntityNameType.Connection]: DEFAULT_ENTRY,
  },
};

@State<EntityNamesStateModel>({
  name: 'entityNames',
  defaults: DEFAULT_STATE,
})
@Injectable()
export class EntityNamesState {
  private readonly projectsService = inject(ProjectsService);
  private readonly modulesService = inject(ModulesService);
  private readonly connectionProfilesService = inject(ConnectionProfilesService);

  @Selector()
  static names(state: EntityNamesStateModel): Record<EntityNameType, readonly string[]> {
    const { entries } = state;
    return {
      [EntityNameType.Project]: entries[EntityNameType.Project].names,
      [EntityNameType.Module]: entries[EntityNameType.Module].names,
      [EntityNameType.Connection]: entries[EntityNameType.Connection].names,
    };
  }

  @Action(LoadEntityNames)
  loadNames(ctx: StateContext<EntityNamesStateModel>, { entityType }: LoadEntityNames) {
    const entry = ctx.getState().entries[entityType];

    if (entry.loaded) {
      return EMPTY;
    }

    return this.getService(entityType)
      .names()
      .pipe(
        tap(({ names }) => {
          const currentState = ctx.getState();
          ctx.patchState({
            entries: {
              ...currentState.entries,
              [entityType]: {
                names: (names ?? []) as readonly string[],
                loaded: true,
              },
            },
          });
        }),
        take(1),
      );
  }

  @Action(AddEntityName)
  addName(ctx: StateContext<EntityNamesStateModel>, { entityType, name }: AddEntityName): void {
    const state = ctx.getState();
    const entry = state.entries[entityType];
    const trimmed = name?.trim() ?? '';
    const normalized = trimmed.toLowerCase();

    if (!normalized || entry.names.map((n) => n.toLowerCase()).includes(normalized)) {
      return;
    }

    ctx.patchState({
      entries: {
        ...state.entries,
        [entityType]: {
          ...entry,
          names: [...entry.names, trimmed],
        },
      },
    });
  }

  @Action(UpdateEntityName)
  updateName(
    ctx: StateContext<EntityNamesStateModel>,
    { entityType, oldName, newName }: UpdateEntityName,
  ): void {
    const state = ctx.getState();
    const entry = state.entries[entityType];

    const normalizedOld = oldName.trim().toLowerCase();
    const next = entry.names
      .map((n) => (n.toLowerCase() === normalizedOld ? newName.trim() : n))
      .filter((n, i, arr) => arr.findIndex((x) => x.toLowerCase() === n.toLowerCase()) === i);

    ctx.patchState({
      entries: {
        ...state.entries,
        [entityType]: {
          ...entry,
          names: next,
        },
      },
    });
  }

  @Action(RemoveEntityName)
  removeName(
    ctx: StateContext<EntityNamesStateModel>,
    { entityType, name }: RemoveEntityName,
  ): void {
    const state = ctx.getState();
    const entry = state.entries[entityType];
    const normalized = name.trim().toLowerCase();

    ctx.patchState({
      entries: {
        ...state.entries,
        [entityType]: {
          ...entry,
          names: entry.names.filter((n) => n.toLowerCase() !== normalized),
        },
      },
    });
  }

  private getService(type: EntityNameType): NamesService {
    switch (type) {
      case EntityNameType.Project:
        return this.projectsService;
      case EntityNameType.Module:
        return this.modulesService;
      case EntityNameType.Connection:
        return this.connectionProfilesService;
    }
  }
}
